import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ColumnDefinition } from '../controls/advanced-gridview/models/column-definition';
import { ColumnType } from '../controls/advanced-gridview/models/column-type';
import { FilterCriteria } from '../controls/advanced-gridview/models/filter-criteria';
import { IFilter } from '../models/interfaces/filter.interface';
import { IFilterable } from '../models/interfaces/filterable.interface';

export class FilterHandler {
    /**
     * Utilisé pour gérer la désincription des observables lorsque la classe est détruite.
     */
    protected classDisposed$: Subject<void> = new Subject<void>();

    /** Formulaire contenant les filtres du tableau de résultats. */
    filtersForm: UntypedFormGroup;

    /** Valeurs des filtres saisis par l'utilisateur. */
    filterValues: IFilter = {};

    /** Définitions des colonnes visibles */
    displayedColumnsDefinitions: ColumnDefinition[];

    /** Définitions des colonnes filtrées */
    filterredColumnDefinitions: ColumnDefinition[];

    /**
     * Constructeur.
     */
    constructor(private sourceComponent: IFilterable) {
        this.filtersForm = new UntypedFormGroup({});
    }

    /**
     * Initialise les filtres du tableau de dossiers.
     * @param displayedColumnsDefinitions : Définitions des colonnes.
     */
    initializeFilters(displayedColumnsDefinitions: ColumnDefinition[]) {
        this.displayedColumnsDefinitions = displayedColumnsDefinitions;

        // Crée un contrôle de filtre dans l'en-tête de chaque colonne.
        this.displayedColumnsDefinitions.forEach((def) => {
            this.initializeFilter(def);
        });
    }

    /**
     * Initialise le filtre d'une colonne.
     * @param columnDef: Definition de la commande.
     */
    initializeFilter(columnDef: ColumnDefinition) {
        // Création du contrôle dans l'en-tête de la colonne.
        this.filtersForm.addControl(columnDef.columnName, new UntypedFormControl());

        // Initialisation du contrôle à vide.
        this.filterValues[columnDef.columnName] = null;

        // Abonnement aux événements ValueChanges du contrôle afin d'appliquer le filtre quand l'utilisateur saisie une valeur.
        this.filtersForm.controls[columnDef.columnName].valueChanges.pipe(takeUntil(this.classDisposed$)).subscribe((val) => {
            if (!!val && val.length != 0) {
                this.applyColumnFilter(val, columnDef);
            } else if (this.filterValues[columnDef.columnName]) {
                // Si le texte saisi est vide mais que la valeur du filtre n'est pas vide.
                this.resetColumnFilter(columnDef.columnName);
            }
        });
    }

    /**
     * Initialise les filtres des attributs.
     * @param attributeFilters : Définitions des attributs.
     */
    initializeAttributeFilters(attributeFilters: ColumnDefinition[]) {
        this.filterredColumnDefinitions = attributeFilters;

        // Crée un contrôle de filtre dans l'en-tête de chaque colonne.
        this.filterredColumnDefinitions.forEach((att) => {
            this.initializeAttributeFilter(att);
        });
    }

    /**
     * Initialise le filtre d'un attribut.
     * @param attributeFilter: Attribut.
     */
    initializeAttributeFilter(attributeFilter: ColumnDefinition) {
        // Création du contrôle dans l'en-tête de la colonne.
        this.filtersForm.addControl(attributeFilter.columnName, new UntypedFormControl());

        // Initialisation du contrôle à vide.
        this.filterValues[attributeFilter.columnName] = null;

        // Abonnement aux événements ValueChanges du contrôle afin d'appliquer le filtre quand l'utilisateur saisie une valeur.
        this.filtersForm.controls[attributeFilter.columnName].valueChanges.pipe(takeUntil(this.classDisposed$)).subscribe((val) => {
            if ((!!val || val === false) && val.length != 0) {
                this.applyAttributeFilter(val, attributeFilter);
            } else if (this.filterValues[attributeFilter.columnName]) {
                // Si le texte saisi est vide mais que la valeur du filtre n'est pas vide.
                this.resetColumnFilter(attributeFilter.columnName);
            }
        });
    }

    /**
     * Spécifie la valeur pour le filtre d'un attribut et l'applique.
     * @param filterValue: Valeur du filtre.
     * @param attributeFilter: Definition de la colonne.
     */
    private applyAttributeFilter(filterValue: any, attributeFilter: ColumnDefinition) {
        if (attributeFilter.type === ColumnType.Date) {
            filterValue = filterValue.format('YYYY-MM-DD');
        }

        let array: string[] = [];

        if (!Array.isArray(filterValue)) {
            array.push(filterValue.toString());
        } else {
            array = filterValue;
        }

        const filterCriteria = new FilterCriteria(attributeFilter.type, array);
        this.filterValues[attributeFilter.columnName] = filterCriteria;

        this.sourceComponent.applyFilters();
    }

    /**
     * Spécifie la valeur pour le filtre d'une colonne et l'applique.
     * @param filterValue: Valeur du filtre.
     * @param columnDef: Definition de la colonne.
     */
    private applyColumnFilter(filterValue: any, columnDef: ColumnDefinition) {
        if (columnDef.type === ColumnType.Date) {
            filterValue = filterValue.format('YYYY-MM-DD');
        }

        let array: string[] = [];

        if (!Array.isArray(filterValue)) {
            array.push(filterValue.toString());
        } else {
            array = filterValue;
        }

        const filterCriteria = new FilterCriteria(columnDef.type, array);
        this.filterValues[columnDef.columnName] = filterCriteria;

        this.sourceComponent.applyFilters();
    }

    /**
     * Réinitialise le filtre de la colonne spécifiée puis applique le filtre.
     * columnDef: Définition de la colonne dont le filtre doit être réinitialisé.
     */
    public resetColumnFilter(columnDef: string): void {
        this.filterValues[columnDef] = null;
        this.filtersForm.controls[columnDef].reset();
        this.sourceComponent.applyFilters();
    }

    /**
     * Réinitialise le filtre de l'attribut spécifiée puis applique le filtre.
     * @param attributeName: Nom de l'attribut filtre qui doit être réinitialisé.
     */
    public resetAttributeFilter(attributeName: string): void {
        this.filterValues[attributeName] = null;
        this.filtersForm.controls[attributeName].reset();
        this.sourceComponent.applyFilters();
    }

    /**
     * Réinitialise les filtres de toutes les colonnes puis applique les filtres.
     */
    public resetAllColumnsFilters(): void {
        if (!!this.displayedColumnsDefinitions) {
            this.displayedColumnsDefinitions.forEach((item, index) => {
                this.filterValues[item.columnName] = null;
            });
        }
        this.filtersForm.reset();
        this.sourceComponent.applyFilters();
    }

    /**
     * Réinitialise les filtres de tous les attributs puis applique les filtres.
     */
    public resetAllAttributesFilters(): void {
        if (!!this.filterredColumnDefinitions) {
            this.filterredColumnDefinitions.forEach((item, index) => {
                this.filterValues[item.columnName] = null;
            });
        }
        this.filtersForm.reset();
        this.sourceComponent.resetSearch();
        this.sourceComponent.applyFilters();
    }

    public dispose() {
        this.classDisposed$.next();
        this.classDisposed$.complete();
    }

    public search(value: string) {
        this.sourceComponent.search(value);
    }
}
